import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { User } from '@/api/types/auth.types';

interface AuthState {
  accessToken: string | null;
  refreshToken: string | null;
  user: User | null;
  isAuthenticated: boolean;
  _hydrated: boolean;

  setTokens: (access: string, refresh: string) => void;
  setUser: (user: User) => void;
  clearAuth: () => void;
  hydrateFromStorage: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      accessToken: null,
      refreshToken: null,
      user: null,
      isAuthenticated: false,
      _hydrated: false,

      setTokens: (access, refresh) =>
        set({
          accessToken: access,
          refreshToken: refresh,
          isAuthenticated: true,
        }),

      setUser: (user) =>
        set({ user }),

      clearAuth: () =>
        set({
          accessToken: null,
          refreshToken: null,
          user: null,
          isAuthenticated: false,
        }),

      /**
       * Re-derive `isAuthenticated` from whatever the persist middleware
       * restored.  Called once during app bootstrap so that a page reload
       * keeps the user logged in when tokens are still in localStorage.
       */
      hydrateFromStorage: () => {
        const { accessToken, refreshToken } = get();
        set({
          isAuthenticated: !!(accessToken && refreshToken),
          _hydrated: true,
        });
      },
    }),
    {
      name: 'photo-improver-auth',
      partialize: (state) => ({
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        user: state.user,
        isAuthenticated: state.isAuthenticated,
      }),
      onRehydrateStorage: () => (state) => {
        // Automatically hydrate when zustand-persist finishes restoring.
        state?.hydrateFromStorage();
      },
    },
  ),
);
